import Tooltip from "@components/ui/tooltip";
import { useIsEditorContext } from "@editor-providers";
import { shallow, useOthersMapped } from "@liveblocks/react/suspense";
import { memo, type ReactNode } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { cn } from "@/lib/cn";
import { CollaborationEnabledProvider } from "../providers";
import type { User } from "../types";
import { hexToRGBA } from "../utils";
import { Suspense } from "./Suspense";

const LOCKED_ENTITIES = ["RichText", "CollapsibleText", "Table"];

export const CoeditingEntityOutline = memo(
  ({ id, name }: { id: string; name?: string }) => {
    const isEditor = useIsEditorContext();
    const { ref, isIntersecting } = useIntersectionObserver({
      rootMargin: "-50px",
      threshold: 0.1,
    });

    if (!isEditor) return null;

    return (
      <>
        <span
          ref={ref}
          className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-0 tw-z-10 tw-size-0"
        ></span>
        {isIntersecting && (
          <Suspense feature="Entity outline">
            <CollaborationEnabledProvider>
              <Outline id={id} name={name ?? ""} />
            </CollaborationEnabledProvider>
          </Suspense>
        )}
      </>
    );
  },
);

const Outline = ({ id, name = "" }: { id: string; name?: string }) => {
  const others = useOthersMapped(
    (other) => ({
      info: other.info as User,
      activeEntityId: other.presence.activeEntityId,
    }),
    shallow,
  );
  if (!others.length) return null;
  const isLocked = LOCKED_ENTITIES.includes(name);
  const firstUser = others[0]?.[1];
  const info = firstUser?.info as User;

  const rgbaColor = hexToRGBA(info?.color as string, 0.05);
  if (firstUser?.activeEntityId !== id) return null;

  return (
    <>
      <WithTooltip
        tooltip={
          isLocked ? `Please wait, ${info?.name} is editing this element` : null
        }
      >
        <div
          {...(isLocked ? { "data-disabled-collaboration": id } : {})}
          data-coediting-outline={name}
          className={cn(
            "tw-pointer-events-none tw-absolute tw-inset-0 tw-left-0 tw-top-0 tw-rounded-[2px] tw-outline tw-outline-2",
            {
              "tw-pointer-events-auto tw-z-[999] tw-cursor-not-allowed":
                isLocked,
            },
          )}
          style={{
            outlineColor: info?.color as string,
          }}
        >
          <div
            className="tw-absolute -tw-right-[2px] -tw-top-[3px] tw-z-20 -tw-mr-[2px] tw-flex -tw-translate-y-full tw-items-center"
            data-coediting-outline-label={name}
          >
            {others.map(([id, user]) => (
              <Label key={id} user={user.info as User} />
            ))}
          </div>
        </div>
      </WithTooltip>

      {isLocked && (
        <span
          className="tw-absolute tw-inset-0 tw-block tw-size-full"
          style={{ background: rgbaColor }}
        ></span>
      )}
    </>
  );
};

const WithTooltip = ({
  children,
  tooltip,
}: {
  children: ReactNode;
  tooltip: string | null;
}) => {
  if (!tooltip) return <>{children}</>;
  return (
    <Tooltip>
      <Tooltip.Trigger>{children}</Tooltip.Trigger>
      <Tooltip.Content className="tw-z-[999]">
        <Tooltip.Text>{tooltip}</Tooltip.Text>
      </Tooltip.Content>
    </Tooltip>
  );
};

const Label = ({ user }: { user: User }) => {
  const initials = user.name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");

  return (
    <div
      className={cn(
        "tw-group tw-pointer-events-auto tw-relative tw-mr-[2px] tw-min-w-6 tw-cursor-pointer tw-whitespace-nowrap tw-rounded-[2px] tw-px-1 tw-font-sans tw-text-[11px]/normal tw-font-medium tw-text-white",
        {
          "tw-text-[#0D0D0D]": user.theme === "light",
        },
      )}
      style={{ backgroundColor: user.color }}
    >
      <span className="tw-absolute tw-left-1/2 tw-top-1/2 tw-block -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transition-opacity tw-duration-150 group-hover:tw-opacity-0">
        {initials}
      </span>
      <span className="tw-block tw-max-w-0 tw-overflow-hidden tw-text-ellipsis tw-transition-all tw-duration-300 group-hover:tw-max-w-36">
        {user.name}
      </span>
    </div>
  );
};
